<template>
  <v-app>
    <Snackbar :SnackBarComponent="SnackBarComponent" v-if="renderComp" />

    <v-main class="login-page">
      <v-row class="fill-height ma-0" no-gutters>
        <!-- Brand panel -->
        <v-col cols="12" md="6" class="brand-panel d-none d-md-flex">
          <div class="brand-orb brand-orb--1" aria-hidden="true"></div>
          <div class="brand-orb brand-orb--2" aria-hidden="true"></div>
          <div class="brand-grid" aria-hidden="true"></div>
          <div class="brand-particles" aria-hidden="true">
            <span v-for="n in 18" :key="n" class="particle"></span>
          </div>

          <div class="brand-content animate-fade-up">
            <div class="brand-logo">
              <div class="brand-logo-mark">
                <v-img
                  src="@/assets/logo2.jpg"
                  alt="ServiceWRK"
                  width="56"
                  height="56"
                  cover
                  class="brand-logo-img"
                />
              </div>
              <div class="brand-logo-text">
                <div class="brand-logo-title">ServiceWRK</div>
                <div class="brand-logo-tagline">Ticket Management</div>
              </div>
            </div>

            <div class="brand-hero">
              <!-- <div class="feature-badge">
                <span class="badge-sparkle">✦</span>
                New · AI-powered ticket routing
              </div> -->
              <h1 class="brand-headline">
                Transform service operations into business growth
              </h1>
              <p class="brand-description">
                Manage tickets, customers, inventory, service requests,
                approvals, attendance and analytics from a single intelligent
                platform.
              </p>
            </div>

            <div class="stats-card animate-float">
              <div class="stats-card-header">
                <span class="stats-card-title">Built for field service teams</span>
              </div>
              <p class="stats-card-lead">
                One workspace for tickets, customers, inventory, and approvals.
              </p>
              <ul class="stats-feature-list" aria-label="Platform capabilities">
                <li class="stats-feature-item">
                  <span class="stats-feature-icon" aria-hidden="true">
                    <v-icon icon="mdi-ticket-outline" size="18" />
                  </span>
                  <span class="stats-feature-text">
                    <strong>Tickets &amp; scheduling</strong>
                    Track work from request to close
                  </span>
                </li>
                <li class="stats-feature-item">
                  <span class="stats-feature-icon" aria-hidden="true">
                    <v-icon icon="mdi-account-group-outline" size="18" />
                  </span>
                  <span class="stats-feature-text">
                    <strong>Customers &amp; fleets</strong>
                    Keep product and location data in sync
                  </span>
                </li>
                <li class="stats-feature-item">
                  <span class="stats-feature-icon" aria-hidden="true">
                    <v-icon icon="mdi-clipboard-check-outline" size="18" />
                  </span>
                  <span class="stats-feature-text">
                    <strong>Approvals &amp; reports</strong>
                    Clear workflows for every role
                  </span>
                </li>
              </ul>
            </div>

            <!-- <div class="brand-footer">
              Trusted by 2,400+ teams &nbsp;·&nbsp; SOC 2 · ISO 27001
            </div> -->
          </div>
        </v-col>

        <!-- Form panel -->
        <v-col cols="12" md="6" class="form-panel">
          <div class="form-glow form-glow--1" aria-hidden="true"></div>
          <div class="form-glow form-glow--2" aria-hidden="true"></div>
          <div class="form-panel-inner animate-slide-in">
            <div class="login-card-shell">
              <v-card class="login-card" elevation="0">
              <v-card-text class="pa-6 pa-sm-8">
                <div class="mobile-brand d-md-none">
                  <div class="brand-logo-mark brand-logo-mark--sm">
                    <v-img
                      src="@/assets/logo2.jpg"
                      alt="ServiceWRK"
                      width="44"
                      height="44"
                      cover
                      class="brand-logo-img"
                    />
                  </div>
                  <div class="mobile-brand-text-wrap">
                    <span class="mobile-brand-text">ServiceWRK</span>
                    <span class="mobile-brand-tagline">Ticket Management</span>
                  </div>
                </div>

                <div class="form-header mb-6">
                  <h2 class="form-title">{{ currentTitle }}</h2>
                  <p class="form-subtitle">{{ currentSubtitle }}</p>
                </div>

                <!-- Sign In / OTP tabs (steps 1 & 5) -->
                <div v-if="step === 1 || step === 5" class="auth-tabs mb-6">
                  <div
                    class="auth-tab-slider"
                    :class="{ 'auth-tab-slider--right': step === 5 }"
                  ></div>
                  <button
                    type="button"
                    class="auth-tab"
                    :class="{ 'auth-tab--active': step === 1 }"
                    @click="switchAuthTab('signin')"
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    class="auth-tab"
                    :class="{ 'auth-tab--active': step === 5 }"
                    @click="switchAuthTab('otp')"
                  >
                    Login with OTP
                  </button>
                </div>

                <v-slide-x-transition mode="out-in">
                  <div :key="step">
                    <!-- Step 1: Email & Password -->
                    <div v-if="step === 1">
                      <v-form ref="form" @submit.prevent="ValidateLoginForm()">
                        <div class="field-group mb-4">
                          <label class="field-label">Email Address</label>
                          <v-text-field
                            v-model="Login.EmailID"
                            placeholder="you@company.com"
                            type="email"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="emailRules"
                            @keyup="mailPasswordLowerCaseMethod(Login.EmailID)"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #prepend-inner>
                              <v-icon color="grey-darken-1" size="20">
                                mdi-email-outline
                              </v-icon>
                            </template>
                          </v-text-field>
                        </div>

                        <div class="field-group mb-2">
                          <div class="field-label-row">
                            <label class="field-label">Password</label>
                            <button
                              type="button"
                              class="link-btn"
                              @click="step = 3"
                            >
                              Forgot?
                            </button>
                          </div>
                          <v-text-field
                            v-model="Login.Password"
                            placeholder="••••••••"
                            :type="isPwd ? 'text' : 'password'"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="[(v) => !!v || 'Password is required']"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #prepend-inner>
                              <v-icon color="grey-darken-1" size="20">
                                mdi-lock-outline
                              </v-icon>
                            </template>
                            <template #append-inner>
                              <v-btn
                                icon
                                variant="text"
                                size="small"
                                @click="isPwd = !isPwd"
                              >
                                <v-icon size="20">
                                  {{ isPwd ? "mdi-eye-off" : "mdi-eye" }}
                                </v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </div>

                        <label
                          class="remember-pill mb-5"
                          :class="{
                            'remember-pill--active': rememberme,
                            'remember-pill--disabled':
                              !Login.EmailID || !Login.Password,
                          }"
                        >
                          <input
                            v-model="rememberme"
                            type="checkbox"
                            class="remember-pill-input"
                            :disabled="!Login.EmailID || !Login.Password"
                          />
                          <span class="remember-pill-toggle" aria-hidden="true">
                            <span class="remember-pill-knob"></span>
                          </span>
                          <span class="remember-pill-label">
                            Remember me for 30 days
                          </span>
                        </label>

                        <v-btn
                          type="submit"
                          :loading="loading"
                          block
                          color="primary"
                          variant="flat"
                          class="primary-btn text-none"
                          height="48"
                        >
                          Sign In
                          <v-icon end size="18" class="btn-arrow"
                            >mdi-arrow-right</v-icon
                          >
                        </v-btn>
                      </v-form>
                    </div>

                    <!-- Step 2: Change Password -->
                    <div v-if="step === 2">
                      <v-form
                        ref="formCP"
                        @submit.prevent="ValidateConfirmPasswordForm()"
                      >
                        <div class="field-group mb-4">
                          <label class="field-label">New Password</label>
                          <v-text-field
                            v-model="Password.NewPassword"
                            :type="isPwdNew ? 'text' : 'password'"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="[(v) => !!v || 'New password is required']"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #append-inner>
                              <v-btn
                                icon
                                variant="text"
                                size="small"
                                @click="isPwdNew = !isPwdNew"
                              >
                                <v-icon size="20">
                                  {{ isPwdNew ? "mdi-eye-off" : "mdi-eye" }}
                                </v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </div>

                        <div class="field-group mb-6">
                          <label class="field-label">Confirm Password</label>
                          <v-text-field
                            v-model="Password.ConfirmPassword"
                            :type="isPwdConfirm ? 'text' : 'password'"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="[
                              (v) => !!v || 'Please confirm your password',
                            ]"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #append-inner>
                              <v-btn
                                icon
                                variant="text"
                                size="small"
                                @click="isPwdConfirm = !isPwdConfirm"
                              >
                                <v-icon size="20">
                                  {{ isPwdConfirm ? "mdi-eye-off" : "mdi-eye" }}
                                </v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </div>

                        <div class="action-row">
                          <v-btn
                            variant="outlined"
                            class="text-none"
                            height="48"
                            @click="step = 1"
                          >
                            <v-icon start>mdi-arrow-left</v-icon>
                            Back
                          </v-btn>
                          <v-btn
                            type="submit"
                            :loading="loading"
                            color="primary"
                            variant="flat"
                            class="primary-btn text-none flex-grow-1"
                            height="48"
                          >
                            Update Password
                          </v-btn>
                        </div>
                      </v-form>
                    </div>

                    <!-- Step 3: Forgot Password -->
                    <div v-if="step === 3">
                      <v-form ref="formOTP" @submit.prevent="ValidateOTP()">
                        <div class="field-group mb-6">
                          <label class="field-label">Email Address</label>
                          <v-text-field
                            v-model="OTPEmail"
                            placeholder="you@company.com"
                            type="email"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="emailRules"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #prepend-inner>
                              <v-icon color="grey-darken-1" size="20">
                                mdi-email-outline
                              </v-icon>
                            </template>
                          </v-text-field>
                        </div>

                        <v-btn
                          type="submit"
                          color="primary"
                          variant="flat"
                          block
                          class="primary-btn text-none mb-3"
                          height="48"
                          :loading="resetCodeLoader"
                        >
                          Send Reset Code
                          <v-icon end>mdi-send</v-icon>
                        </v-btn>
                        <v-btn
                          variant="text"
                          block
                          class="text-none"
                          height="48"
                          @click="step = 1"
                        >
                          <v-icon start>mdi-arrow-left</v-icon>
                          Back to Sign In
                        </v-btn>
                      </v-form>
                    </div>

                    <!-- Step 4: Reset Password -->
                    <div v-if="step === 4" :key="'step4-' + step4Key">
                      <v-form
                        ref="formConfirmOTP"
                        @submit.prevent="ValidateEnteredOTP()"
                      >
                        <div class="field-group mb-4">
                          <label class="field-label">Verification Code</label>
                          <v-text-field
                            v-model="ConfirmOTP"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="[
                              (v) => !!v || 'Verification code is required',
                            ]"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          />
                        </div>

                        <div class="field-group mb-6">
                          <label class="field-label">New Password</label>
                          <v-text-field
                            v-model="ConfirmNewPassword"
                            :type="isPwdConfirmCode ? 'text' : 'password'"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="[(v) => !!v || 'New password is required']"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #append-inner>
                              <v-btn
                                icon
                                variant="text"
                                size="small"
                                @click="isPwdConfirmCode = !isPwdConfirmCode"
                              >
                                <v-icon size="20">
                                  {{
                                    isPwdConfirmCode ? "mdi-eye-off" : "mdi-eye"
                                  }}
                                </v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </div>

                        <v-btn
                          type="submit"
                          color="primary"
                          variant="flat"
                          block
                          class="primary-btn text-none mb-3"
                          height="48"
                        >
                          Reset Password
                        </v-btn>
                        <v-btn
                          variant="text"
                          block
                          class="text-none"
                          height="48"
                          @click="step = 1"
                        >
                          Cancel
                        </v-btn>
                      </v-form>
                    </div>

                    <!-- Step 5: OTP Email -->
                    <div v-if="step === 5">
                      <v-form
                        ref="loginWithOPT"
                        @submit.prevent="getOptMethods()"
                      >
                        <div class="field-group mb-6">
                          <label class="field-label">Email Address</label>
                          <v-text-field
                            v-model="EmailForOPT"
                            placeholder="you@company.com"
                            type="email"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="emailRules"
                            @keyup="mailOtpLowerCaseMethod(EmailForOPT)"
                            color="primary"
                            class="rounded-field"
                            hide-details="auto"
                          >
                            <template #prepend-inner>
                              <v-icon color="grey-darken-1" size="20">
                                mdi-email-outline
                              </v-icon>
                            </template>
                          </v-text-field>
                        </div>

                        <v-btn
                          type="submit"
                          :loading="loading_get_otp_btn"
                          block
                          color="primary"
                          variant="flat"
                          class="primary-btn text-none"
                          height="48"
                        >
                          <v-icon start>mdi-key</v-icon>
                          Get OTP
                        </v-btn>
                      </v-form>
                    </div>

                    <!-- Step 6: OTP Verification -->
                    <div v-if="step === 6">
                      <v-form
                        ref="form_enter_otp_step"
                        @submit.prevent="validateOPTMethod()"
                      >
                        <div class="text-center mb-6">
                          <v-otp-input
                            v-model="opt_to_login"
                            length="6"
                            type="number"
                            variant="outlined"
                            density="comfortable"
                            rounded="xl"
                            :rules="[(v) => !!v || 'OTP is required']"
                            class="otp-input-group"
                          />
                          <p class="otp-hint mt-3">
                            Enter the 6-digit code sent to
                            <strong>{{ EmailForOPT }}</strong>
                          </p>
                        </div>

                        <v-btn
                          type="submit"
                          :loading="loading_btn_check_otp"
                          block
                          color="primary"
                          variant="flat"
                          class="primary-btn text-none mb-3"
                          height="48"
                        >
                          Verify & Sign In
                        </v-btn>
                        <v-btn
                          block
                          variant="text"
                          class="text-none"
                          height="48"
                          @click="step = 5"
                        >
                          <v-icon start>mdi-arrow-left</v-icon>
                          Go Back
                        </v-btn>
                      </v-form>
                    </div>
                  </div>
                </v-slide-x-transition>
              </v-card-text>
              </v-card>
            </div>

            <p class="copyright text-center mt-4">
              © {{ current_year }} ServiceWRK. All rights reserved.
            </p>
          </div>
        </v-col>
      </v-row>
    </v-main>
  </v-app>
</template>

<script>
import {
  confirmSignIn,
  signIn,
  signOut,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";
import Snackbar from "@/components/Extras/SnackbarView.vue";

export default {
  components: {
    Snackbar,
  },
  data: () => ({
    renderComp: true,
    loading_get_otp_btn: false,
    loading_btn_check_otp: false,
    step: 1,
    Login: {
      EmailID: "",
      Password: "",
    },
    EmailForOPT: "",
    opt_to_login: "",
    ConfirmOTP: "",
    ConfirmNewPassword: "",
    OTPEmail: "",
    isPwdNew: false,
    isPwdConfirm: false,
    isPwdConfirmCode: false,
    Password: {
      NewPassword: "",
      ConfirmPassword: "",
    },
    isPwd: false,
    loading: false,
    rememberme: false,
    user: {},
    SnackBarComponent: {},
    current_year: new Date().getFullYear(),
    step4Key: 0,
    resetCodeLoader: false,
    emailRules: [
      (v) => !!v || "Email is required",
      (v) => /.+@.+/.test(v) || "Email must be valid",
    ],
  }),
  computed: {
    currentTitle() {
      const titles = {
        1: "Welcome back",
        2: "Change Password",
        3: "Reset Password",
        4: "Verify Code",
        5: "Welcome back",
        6: "Enter OTP",
      };
      return titles[this.step] || "Welcome back";
    },
    currentSubtitle() {
      const subtitles = {
        1: "Sign in to your ServiceWRK workspace",
        2: "Please set a new password for your account",
        3: "Enter your email to receive a reset code",
        4: "Enter the verification code and new password",
        5: "Sign in to your ServiceWRK workspace",
        6: "Enter the 6-digit code sent to your email",
      };
      return subtitles[this.step] || "Sign in to your ServiceWRK workspace";
    },
  },
  watch: {
    step(newVal) {
      if (newVal === 4) {
        this.ConfirmOTP = "";
        this.ConfirmNewPassword = "";
        this.step4Key++;
      }

      if (newVal === 3) {
        this.OTPEmail = "";
      }
    },

    rememberme(val) {
      if (val) {
        localStorage.setItem("currentuseremail", JSON.stringify(this.Login));
      } else {
        localStorage.removeItem("currentuseremail");
      }
    },
  },
  mounted() {
    this.CheckRememberMe();
  },
  methods: {
    switchAuthTab(tab) {
      this.step = tab === "otp" ? 5 : 1;
    },

    mailOtpLowerCaseMethod(EmailForOPT) {
      this.EmailForOPT = EmailForOPT.trim().toLowerCase();
    },

    mailPasswordLowerCaseMethod(mail) {
      this.Login.EmailID = mail.trim().toLowerCase();
    },

    async validateOPTMethod() {
      const isValid = await this.$refs.form_enter_otp_step.validate();
      if (isValid.valid) {
        this.confirmOTPForLogin();
      } else {
        this.showSnackbar("Enter OTP", "red");
      }
    },

    async confirmOTPForLogin() {
      try {
        this.loading_btn_check_otp = true;
        const otpValidationResult = await confirmSignIn({
          challengeResponse: this.opt_to_login,
        });

        if (otpValidationResult.isSignedIn) {
          this.showSnackbar("Logged in successfully!", "green");
          this.ActivateMethod();
        } else {
          this.showSnackbar("Invalid OTP entered", "red");
        }
        this.loading_btn_check_otp = false;
      } catch (error) {
        this.showSnackbar(error.message, "red");
        this.loading_btn_check_otp = false;
      }
    },

    async getOptMethods() {
      const isValid = await this.$refs.loginWithOPT.validate();
      if (isValid.valid) {
        this.opt_login();
      } else {
        this.showSnackbar("Please enter a valid email address", "red");
      }
    },

    async opt_login() {
      try {
        this.loading_get_otp_btn = true;
        this.user = await signIn({
          username: this.EmailForOPT.toLowerCase().trim(),
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });

        this.$store.commit("SET_USEREMAIL", this.EmailForOPT);

        if (
          this.user.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
        ) {
          this.step = 6;
          this.showSnackbar("OTP sent successfully!", "green");
        }
        this.loading_get_otp_btn = false;
      } catch (error) {
        await signOut();
        this.SignIn();
        this.loading_get_otp_btn = false;
      }
    },

    CheckRememberMe() {
      const savedLogin = localStorage.getItem("currentuseremail");
      if (savedLogin) {
        this.Login = JSON.parse(savedLogin);
        this.rememberme = true;
      }
    },

    async ValidateEnteredOTP() {
      this.OTPEmail = this.OTPEmail.trim();
      this.ConfirmOTP = this.ConfirmOTP.trim();
      this.ConfirmNewPassword = this.ConfirmNewPassword.trim();

      const isValid = await this.$refs.formConfirmOTP.validate();
      if (isValid.valid) {
        this.ConfirmOTPMethod();
      } else {
        this.showSnackbar("Please enter the code and new password", "red");
      }
    },
    ConfirmOTPMethod() {
      confirmResetPassword({
        username: this.OTPEmail,
        confirmationCode: this.ConfirmOTP,
        newPassword: this.ConfirmNewPassword,
      })
        .then(() => {
          this.$refs.formConfirmOTP.reset();
          this.renderComp = false;
          this.$nextTick(() => {
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "green",
              Top: true,
              SnackbarText: "Password Successfully Changed",
            };
            this.renderComp = true;
          });
          this.step = 1;
        })
        .catch((err) => {
          this.renderComp = false;
          this.$nextTick(() => {
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "red",
              Top: true,
              SnackbarText: err.message,
            };
            this.renderComp = true;
          });
        });
    },

    async ValidateOTP() {
      this.OTPEmail = this.OTPEmail.trim();
      const isValid = await this.$refs.formOTP.validate();
      if (isValid.valid) {
        this.SendOTPEmail();
        this.resetCodeLoader = true;
      } else {
        this.showSnackbar("Please enter a valid email address", "red");
      }
    },
    SendOTPEmail() {
      resetPassword({ username: this.OTPEmail })
        .then(() => {
          this.ConfirmOTP = "";
          this.ConfirmNewPassword = "";
          this.step4Key++;

          this.renderComp = false;
          this.$nextTick(() => {
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "green",
              Top: true,
              SnackbarText: "OTP Sent To Registered Email",
            };
            this.renderComp = true;
          });

          this.step = 4;
          setTimeout(() => {
            this.ConfirmOTP = "";
            this.ConfirmNewPassword = "";
          }, 1000);
        })
        .catch((err) => {
          this.renderComp = false;
          this.$nextTick(() => {
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "red",
              Top: true,
              SnackbarText: err.message,
            };
            this.renderComp = true;
          });
        });
      this.resetCodeLoader = false;
    },

    async ValidateConfirmPasswordForm() {
      this.Password.NewPassword = this.Password.NewPassword.trim();
      this.Password.ConfirmPassword = this.Password.ConfirmPassword.trim();
      const isValid = await this.$refs.formCP.validate();
      if (isValid.valid) {
        if (
          this.Password.NewPassword != "" &&
          this.Password.NewPassword != null &&
          this.Password.ConfirmPassword != "" &&
          this.Password.ConfirmPassword != null
        ) {
          if (this.Password.NewPassword == this.Password.ConfirmPassword) {
            this.ConfirmPassword();
          } else {
            this.$refs.form.reset();
            this.renderComp = false;
            this.$nextTick(() => {
              this.SnackBarComponent = {
                SnackbarVmodel: true,
                SnackbarColor: "red",
                Top: true,
                SnackbarText: "Password does not match",
              };
              this.renderComp = true;
            });
          }
        }
      } else {
        this.renderComp = false;
        this.$nextTick(() => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            SnackbarText: "Fields marked with asterisks (*) are mandatory",
          };
          this.renderComp = true;
        });
      }
    },

    async ConfirmPassword() {
      try {
        this.loading = true;
        const result = await confirmSignIn({
          challengeResponse: this.Password.ConfirmPassword,
        });
        console.log("NEW_PASSW", result);
        this.$refs.formCP.reset();
        this.loading = false;
        this.renderComp = false;
        this.$nextTick(() => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "Login Successful",
          };
          this.renderComp = true;
        });
        this.ActivateMethod();
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.renderComp = false;
        this.$nextTick(() => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            SnackbarText: error.message,
          };
          this.renderComp = true;
        });
      }
    },

    async ValidateLoginForm() {
      this.Login.EmailID = this.Login.EmailID.trim();
      this.Login.Password = this.Login.Password.trim();

      const isValid = await this.$refs.form.validate();
      if (isValid.valid) {
        this.SignIn();
      } else {
        this.showSnackbar("Please fill all required fields", "red");
      }
    },

    async SignIn() {
      try {
        this.loading = true;
        const user = await signIn({
          username: this.Login.EmailID,
          password: this.Login.Password,
          options: {
            authFlowType: "USER_SRP_AUTH",
          },
        });

        this.user = user;
        this.$store.commit("SET_USEREMAIL", this.Login.EmailID);

        if (
          user.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          this.step = 2;
          this.loading = false;
        } else {
          this.showSnackbar("Login successful", "green");
          this.ActivateMethod();
        }
      } catch (error) {
        if (error.message === "There is already a signed in user.") {
          await signOut();
          this.SignIn();
          return;
        }
        this.loading = false;
        this.showSnackbar(error.message, "red");
      }
    },

    ActivateMethod() {
      this.loading = false;
      setTimeout(() => this.$router.push("/LandingPage"), 500);
    },

    showSnackbar(message, color) {
      this.renderComp = false;
      this.$nextTick(() => {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: color,
          Top: true,
          SnackbarText: message,
        };
        this.renderComp = true;
      });
    },
  },
};
</script>

<style scoped>
/* Brand tokens aligned with ServiceWRK theme + mockup */
.login-page {
  --sw-primary: #7b046c;
  --sw-primary-light: #a21caf;
  --sw-primary-dark: #5c034f;
  --sw-secondary: #bd58b0;
  --sw-accent-green: #4ade80;
  --sw-surface: #faf8fc;
  --sw-card-border: rgba(123, 4, 108, 0.08);
  --sw-text: #111827;
  --sw-text-muted: #6b7280;
  --sw-tab-bg: #f0edf3;
  min-height: 100vh;
  background: var(--sw-surface);
}

.fill-height {
  min-height: 100vh;
}

/* Brand panel */
.brand-panel {
  background: linear-gradient(
    155deg,
    #5c034f 0%,
    var(--sw-primary) 38%,
    var(--sw-primary-light) 72%,
    #c026d3 100%
  );
  background-size: 200% 200%;
  animation: gradientShift 14s ease infinite;
  position: relative;
  overflow: hidden;
}

.brand-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 15% 85%,
    rgba(189, 88, 176, 0.4) 0%,
    transparent 50%
  );
  pointer-events: none;
}

.brand-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
  pointer-events: none;
}

.brand-orb--1 {
  width: 320px;
  height: 320px;
  background: rgba(192, 38, 211, 0.55);
  top: -80px;
  right: -60px;
  animation: orbDrift 18s ease-in-out infinite;
}

.brand-orb--2 {
  width: 260px;
  height: 260px;
  background: rgba(123, 4, 108, 0.6);
  bottom: 10%;
  left: -40px;
  animation: orbDrift 22s ease-in-out infinite reverse;
}

.brand-grid {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.04) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, black 20%, transparent 75%);
  pointer-events: none;
}

.brand-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.4);
  animation: particleFloat 8s ease-in-out infinite;
}

.particle:nth-child(1) {
  top: 12%;
  left: 18%;
  animation-delay: 0s;
}
.particle:nth-child(2) {
  top: 28%;
  left: 72%;
  animation-delay: 1.2s;
}
.particle:nth-child(3) {
  top: 45%;
  left: 35%;
  animation-delay: 2.4s;
}
.particle:nth-child(4) {
  top: 62%;
  left: 85%;
  animation-delay: 0.8s;
}
.particle:nth-child(5) {
  top: 78%;
  left: 22%;
  animation-delay: 3.1s;
}
.particle:nth-child(6) {
  top: 8%;
  left: 55%;
  animation-delay: 1.8s;
}
.particle:nth-child(7) {
  top: 55%;
  left: 8%;
  animation-delay: 4s;
}
.particle:nth-child(8) {
  top: 35%;
  left: 92%;
  animation-delay: 2s;
}
.particle:nth-child(9) {
  top: 88%;
  left: 60%;
  animation-delay: 0.5s;
}
.particle:nth-child(10) {
  top: 20%;
  left: 42%;
  animation-delay: 3.5s;
}
.particle:nth-child(11) {
  top: 70%;
  left: 48%;
  animation-delay: 1.5s;
}
.particle:nth-child(12) {
  top: 50%;
  left: 65%;
  animation-delay: 2.8s;
}
.particle:nth-child(13) {
  top: 15%;
  left: 80%;
  animation-delay: 4.2s;
}
.particle:nth-child(14) {
  top: 82%;
  left: 38%;
  animation-delay: 0.3s;
}
.particle:nth-child(15) {
  top: 40%;
  left: 15%;
  animation-delay: 3.8s;
}
.particle:nth-child(16) {
  top: 65%;
  left: 75%;
  animation-delay: 1.1s;
}
.particle:nth-child(17) {
  top: 92%;
  left: 12%;
  animation-delay: 2.6s;
}
.particle:nth-child(18) {
  top: 5%;
  left: 30%;
  animation-delay: 4.5s;
}

.brand-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 48px 56px;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-logo-mark {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  padding: 4px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.brand-logo-mark--sm {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  padding: 3px;
}

.brand-logo-img {
  border-radius: 14px;
}

.brand-logo-mark--sm .brand-logo-img {
  border-radius: 11px;
}

.brand-logo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-logo-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
  line-height: 1.15;
}

.brand-logo-tagline {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.82);
  letter-spacing: 0.01em;
  line-height: 1.3;
}

.badge-sparkle {
  margin-right: 6px;
  font-size: 0.7rem;
  opacity: 0.9;
}

.feature-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.78rem;
  font-weight: 500;
  margin-bottom: 24px;
  backdrop-filter: blur(12px);
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.08);
  animation: badgeGlow 4s ease-in-out infinite;
}

.brand-headline {
  font-size: clamp(1.75rem, 2.5vw, 2.35rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 16px;
  max-width: 480px;
}

.brand-description {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.65;
  max-width: 460px;
  margin: 0;
}

.stats-card {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 18px;
  padding: 20px 22px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  margin-top: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.stats-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-card-title {
  font-size: 0.95rem;
  font-weight: 650;
  color: #fff;
  letter-spacing: 0.01em;
}

.stats-card-lead {
  margin: 10px 0 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
  max-width: 36ch;
}

.stats-feature-list {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stats-feature-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.stats-feature-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.stats-feature-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.72);
}

.stats-feature-text strong {
  font-size: 0.8125rem;
  font-weight: 650;
  color: #fff;
}

.brand-footer {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 32px;
}

/* Form panel */
.form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    165deg,
    #faf8fc 0%,
    #f4ebf9 38%,
    #ebe0f3 72%,
    #f7f2fb 100%
  );
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
}

.form-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(48px);
}

.form-glow--1 {
  top: -8%;
  right: -6%;
  width: min(420px, 55vw);
  height: min(420px, 55vw);
  background: radial-gradient(
    circle,
    rgba(192, 38, 211, 0.22) 0%,
    rgba(123, 4, 108, 0.08) 45%,
    transparent 72%
  );
  animation: orbDrift 20s ease-in-out infinite;
}

.form-glow--2 {
  bottom: 6%;
  left: -8%;
  width: min(340px, 48vw);
  height: min(340px, 48vw);
  background: radial-gradient(
    circle,
    rgba(123, 4, 108, 0.16) 0%,
    rgba(162, 28, 175, 0.06) 50%,
    transparent 72%
  );
  animation: orbDrift 24s ease-in-out infinite reverse;
}

.form-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse at 88% 12%,
      rgba(192, 38, 211, 0.12) 0%,
      transparent 48%
    ),
    radial-gradient(
      ellipse at 8% 88%,
      rgba(123, 4, 108, 0.1) 0%,
      transparent 42%
    );
  pointer-events: none;
}

.form-panel-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
}

.mobile-brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mobile-brand-text-wrap {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.mobile-brand-text {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--sw-primary);
  line-height: 1.2;
}

.mobile-brand-tagline {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--sw-text-muted);
  line-height: 1.2;
}

.login-card-shell {
  --login-notch-r: 14px;
  --login-notch-top: 50%;
  position: relative;
  width: 100%;
  filter:
    drop-shadow(0 8px 32px rgba(123, 4, 108, 0.14))
    drop-shadow(0 2px 10px rgba(123, 4, 108, 0.06));
  transition: filter 0.35s ease;
}

.login-card-shell:hover {
  filter:
    drop-shadow(0 12px 40px rgba(123, 4, 108, 0.18))
    drop-shadow(0 4px 14px rgba(123, 4, 108, 0.08));
}

.login-card {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.72) !important;
  background: rgba(255, 255, 255, 0.52) !important;
  backdrop-filter: blur(24px) saturate(1.45);
  -webkit-backdrop-filter: blur(24px) saturate(1.45);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82) !important;
  -webkit-mask-image:
    linear-gradient(#000 0 0),
    radial-gradient(
      circle var(--login-notch-r) at 0 var(--login-notch-top),
      #000 99.5%,
      transparent 100%
    ),
    radial-gradient(
      circle var(--login-notch-r) at 100% var(--login-notch-top),
      #000 99.5%,
      transparent 100%
    );
  -webkit-mask-composite: source-out, source-out;
  mask-image:
    linear-gradient(#000 0 0),
    radial-gradient(
      circle var(--login-notch-r) at 0 var(--login-notch-top),
      #000 99.5%,
      transparent 100%
    ),
    radial-gradient(
      circle var(--login-notch-r) at 100% var(--login-notch-top),
      #000 99.5%,
      transparent 100%
    );
  mask-composite: exclude, exclude;
  transition:
    border-color 0.35s ease,
    transform 0.35s ease;
}

.login-card :deep(.v-card-text) {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
}

.login-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    148deg,
    rgba(255, 255, 255, 0.78) 0%,
    rgba(248, 240, 252, 0.52) 45%,
    rgba(192, 38, 211, 0.1) 100%
  );
  pointer-events: none;
  z-index: 0;
}

.login-card::after {
  content: "";
  position: absolute;
  top: -35%;
  right: -18%;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(192, 38, 211, 0.2) 0%,
    transparent 68%
  );
  pointer-events: none;
  z-index: 0;
}

.login-card:hover {
  border-color: rgba(255, 255, 255, 0.88) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
}

.form-title {
  font-size: 1.625rem;
  font-weight: 700;
  color: var(--sw-text);
  margin-bottom: 6px;
  letter-spacing: -0.02em;
}

.form-subtitle {
  font-size: 0.9rem;
  color: var(--sw-text-muted);
  margin: 0;
}

.auth-tabs {
  display: flex;
  position: relative;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(123, 4, 108, 0.12);
  border-radius: 999px;
  padding: 5px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.auth-tab-slider {
  position: absolute;
  top: 5px;
  left: 5px;
  width: calc(50% - 5px);
  height: calc(100% - 10px);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow:
    0 2px 12px rgba(123, 4, 108, 0.14),
    0 0 0 1px rgba(255, 255, 255, 0.7);
  transition: transform 0.4s cubic-bezier(0.34, 1.2, 0.64, 1);
  z-index: 0;
}

.auth-tab-slider--right {
  transform: translateX(100%);
}

.auth-tab {
  flex: 1;
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  padding: 11px 18px;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--sw-text-muted);
  cursor: pointer;
  transition: color 0.3s ease;
}

.auth-tab--active {
  color: var(--sw-text);
}

.field-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.field-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.field-label-row .field-label {
  margin-bottom: 0;
}

.link-btn {
  border: none;
  background: none;
  color: var(--sw-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.link-btn:hover {
  color: var(--sw-primary-light);
  text-decoration: underline;
}

.rounded-field :deep(.v-field) {
  border-radius: 16px !important;
  min-height: 48px;
  background: rgba(255, 255, 255, 0.72) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
}

.rounded-field :deep(.v-field__outline) {
  --v-field-border-opacity: 0.22;
}

.rounded-field :deep(.v-field:hover .v-field__outline) {
  --v-field-border-opacity: 0.4;
}

.rounded-field :deep(.v-field--focused) {
  box-shadow: 0 0 0 4px rgba(123, 4, 108, 0.1);
}

.rounded-field :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
}

/* Custom remember-me pill with toggle switch */
.remember-pill {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(123, 4, 108, 0.12);
  background: rgba(255, 255, 255, 0.42);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: pointer;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  user-select: none;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.remember-pill:hover:not(.remember-pill--disabled) {
  border-color: rgba(123, 4, 108, 0.28);
  background: rgba(255, 255, 255, 0.58);
}

.remember-pill--active {
  border-color: rgba(123, 4, 108, 0.34);
  background: linear-gradient(
    90deg,
    rgba(123, 4, 108, 0.1) 0%,
    rgba(192, 38, 211, 0.14) 100%
  );
  box-shadow:
    0 2px 14px rgba(123, 4, 108, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.remember-pill--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.remember-pill-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.remember-pill-toggle {
  flex-shrink: 0;
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.35s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.remember-pill--active .remember-pill-toggle {
  background: linear-gradient(90deg, #7b046c 0%, #c026d3 100%);
  box-shadow: 0 2px 8px rgba(123, 4, 108, 0.35);
}

.remember-pill-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.35s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.remember-pill--active .remember-pill-knob {
  transform: translateX(20px);
}

.remember-pill-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #4b5563;
  line-height: 1.3;
}

.remember-pill--active .remember-pill-label {
  color: var(--sw-primary);
  font-weight: 600;
}

.primary-btn {
  position: relative;
  overflow: hidden;
  border-radius: 999px !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
  color: #fff !important;
  background: linear-gradient(
    90deg,
    #7b046c 0%,
    #a21caf 42%,
    #c026d3 72%,
    #d61de2 100%
  ) !important;
  transition: transform 0.25s ease, box-shadow 0.25s ease !important;
  box-shadow: 0 4px 16px rgba(123, 4, 108, 0.4),
    0 2px 8px rgba(214, 29, 226, 0.25) !important;
}

.primary-btn :deep(.v-btn__overlay),
.primary-btn :deep(.v-btn__underlay) {
  opacity: 0 !important;
}

.primary-btn :deep(.v-btn__content),
.primary-btn :deep(.v-icon) {
  color: #fff !important;
  position: relative;
  z-index: 1;
}

.primary-btn::after {
  content: "";
  position: absolute;
  top: 0;
  left: -80%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.28),
    transparent
  );
  animation: btnShimmer 4.5s ease-in-out infinite;
  pointer-events: none;
}

.primary-btn:hover {
  transform: translateY(-1px);
  background: linear-gradient(
    90deg,
    #8b2cf5 0%,
    #b91cc4 42%,
    #d946ef 72%,
    #e879f9 100%
  ) !important;
  box-shadow: 0 6px 24px rgba(139, 44, 245, 0.45),
    0 4px 12px rgba(214, 29, 226, 0.35) !important;
}

.primary-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(123, 4, 108, 0.35),
    0 1px 4px rgba(214, 29, 226, 0.2) !important;
}

.btn-arrow {
  transition: transform 0.25s ease;
}

.primary-btn:hover .btn-arrow {
  transform: translateX(3px);
}

.action-row {
  display: flex;
  gap: 12px;
}

.action-row .v-btn {
  border-radius: 999px !important;
}

.otp-input-group {
  gap: 10px;
  justify-content: center;
}

.otp-input-group :deep(.v-field) {
  height: 56px;
  width: 48px;
  border-radius: 16px !important;
  font-size: 1.25rem;
  font-weight: 600;
  transition: box-shadow 0.3s ease;
}

.otp-input-group :deep(.v-field--focused) {
  box-shadow: 0 0 0 3px rgba(123, 4, 108, 0.12);
}

.otp-hint {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.copyright {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0;
}

/* Entrance & motion */
.animate-fade-up {
  animation: fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.animate-slide-in {
  animation: slideIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
}

.animate-float {
  animation: cardFloat 6s ease-in-out infinite;
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes orbDrift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -20px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 15px) scale(0.95);
  }
}

@keyframes particleFloat {
  0%,
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-18px) scale(1.2);
    opacity: 0.9;
  }
}

@keyframes badgeGlow {
  0%,
  100% {
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.08);
  }
  50% {
    box-shadow: 0 0 28px rgba(255, 255, 255, 0.18);
  }
}

@keyframes cardFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes btnShimmer {
  0%,
  100% {
    left: -80%;
  }
  50% {
    left: 130%;
  }
}

:deep(input:-webkit-autofill) {
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: #333 !important;
}

:deep(.v-field__outline) {
  --v-field-border-opacity: 0.25;
}

:deep(.v-field:hover .v-field__outline) {
  --v-field-border-opacity: 0.45;
}

:deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
}

@media (max-width: 960px) {
  .login-page {
    min-height: 100vh;
    min-height: 100dvh;
    overflow-x: hidden;
  }

  .fill-height {
    min-height: 100vh;
    min-height: 100dvh;
  }

  .form-panel {
    min-height: 100vh;
    min-height: 100dvh;
    align-items: center;
    justify-content: center;
    padding: 24px 18px;
    padding:
      max(24px, env(safe-area-inset-top))
      18px
      max(24px, env(safe-area-inset-bottom));
    overflow-y: auto;
    overflow-x: hidden;
    background:
      radial-gradient(
        circle at 15% 8%,
        rgba(123, 4, 108, 0.16) 0%,
        transparent 32%
      ),
      linear-gradient(165deg, #fff 0%, #f7eefb 48%, #f3e6fa 100%);
  }

  .form-panel-inner {
    width: min(100%, 390px);
    max-width: 390px;
    margin: 0 auto;
  }

  .form-glow {
    opacity: 0.65;
    filter: blur(36px);
  }

  .mobile-brand {
    margin-bottom: 18px !important;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    text-align: center;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-brand .brand-logo-mark {
    width: 44px;
    height: 44px;
    border-radius: 13px;
    box-shadow: 0 2px 12px rgba(123, 4, 108, 0.15);
  }

  .mobile-brand-text {
    font-size: clamp(1.05rem, 4vw, 1.25rem);
    letter-spacing: -0.02em;
  }

  .mobile-brand-text-wrap {
    align-items: center;
  }

  .mobile-brand-tagline {
    font-size: 0.72rem;
  }

  .login-card-shell {
    --login-notch-r: 0;
    width: 100%;
    max-width: 390px;
    filter: drop-shadow(0 10px 28px rgba(123, 4, 108, 0.12));
  }

  .login-card-shell:hover {
    filter: drop-shadow(0 10px 28px rgba(123, 4, 108, 0.12));
  }

  .login-card {
    border-radius: 24px !important;
    background: rgba(255, 255, 255, 0.78) !important;
    border: 1px solid rgba(255, 255, 255, 0.88) !important;
    -webkit-mask-image: none !important;
    mask-image: none !important;
    backdrop-filter: blur(18px) saturate(1.2);
    -webkit-backdrop-filter: blur(18px) saturate(1.2);
  }

  .login-card :deep(.v-card-text) {
    padding: 22px 22px 20px !important;
  }

  .login-card::after {
    width: 180px;
    height: 180px;
    top: -60px;
    right: -70px;
    opacity: 0.65;
  }

  .form-header {
    margin-bottom: 16px !important;
    text-align: center;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .form-title {
    font-size: clamp(1.45rem, 6vw, 1.85rem);
    line-height: 1.15;
    margin-bottom: 5px;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-brand-text::selection,
  .mobile-brand-tagline::selection,
  .form-title::selection,
  .form-subtitle::selection {
    background: transparent;
  }

  .mobile-brand-text::-moz-selection,
  .mobile-brand-tagline::-moz-selection,
  .form-title::-moz-selection,
  .form-subtitle::-moz-selection {
    background: transparent;
  }

  .form-subtitle {
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .auth-tabs {
    margin-bottom: 20px !important;
    padding: 4px;
    background: rgba(255, 255, 255, 0.66);
  }

  .auth-tab-slider {
    top: 4px;
    left: 4px;
    width: calc(50% - 4px);
    height: calc(100% - 8px);
  }

  .auth-tab {
    min-height: 44px;
    padding: 10px 8px;
    font-size: 0.86rem;
    white-space: nowrap;
  }

  .field-group {
    margin-bottom: 14px !important;
  }

  .field-label,
  .link-btn {
    font-size: 0.82rem;
  }

  .rounded-field :deep(.v-field) {
    min-height: 48px;
    border-radius: 15px !important;
    background: rgba(255, 255, 255, 0.86) !important;
  }

  .rounded-field :deep(.v-field__input) {
    min-height: 46px;
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 0.92rem;
  }

  .remember-pill {
    margin-bottom: 18px !important;
    padding: 9px 12px;
    gap: 10px;
    background: rgba(255, 255, 255, 0.66);
  }

  .remember-pill-toggle {
    width: 42px;
    height: 24px;
  }

  .remember-pill-label {
    font-size: 0.84rem;
  }

  .primary-btn {
    height: 50px !important;
    min-height: 50px !important;
  }

  .copyright {
    margin-top: 18px !important;
    padding-bottom: 4px;
    font-size: 0.76rem;
    width: 100%;
    max-width: 390px;
  }
}

@media (max-width: 430px) {
  .form-panel {
    padding: 20px 14px;
    padding:
      max(20px, env(safe-area-inset-top))
      14px
      max(20px, env(safe-area-inset-bottom));
  }

  .form-panel-inner,
  .login-card-shell {
    max-width: 360px;
  }

  .copyright {
    max-width: 360px;
  }

  .login-card :deep(.v-card-text) {
    padding: 20px 18px 18px !important;
  }

  .mobile-brand .brand-logo-mark {
    width: 42px;
    height: 42px;
  }

  .auth-tab {
    font-size: 0.82rem;
  }
}

@media (max-width: 360px) {
  .form-panel-inner,
  .login-card-shell {
    max-width: 330px;
  }

  .copyright {
    max-width: 330px;
  }

  .login-card :deep(.v-card-text) {
    padding: 18px 14px !important;
  }

  .auth-tab {
    font-size: 0.78rem;
    letter-spacing: -0.01em;
  }

  .remember-pill-label {
    font-size: 0.78rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .brand-panel,
  .brand-orb,
  .form-glow,
  .particle,
  .feature-badge,
  .stats-card,
  .animate-fade-up,
  .animate-slide-in,
  .primary-btn,
  .primary-btn::after,
  .btn-arrow,
  .auth-tab-slider {
    animation: none !important;
    transition: none !important;
  }

  .primary-btn:hover {
    transform: none;
  }
}
</style>
